"use client";

import { useEffect, useRef, useState } from "react";
import { captureCustomer } from "@/lib/actions/captureCustomer";
import { EMAIL_CAPTURE_SURFACE, EMAIL_CAPTURED, EMAIL_CAPTURED_EVENT, type StateSource } from "@/lib/marketing/email-capture-policy";
import { normalizeUsState, type UsState } from "@/lib/us-states";
import { phCapture, phGetDistinctId } from "@/utils/analytics";
import { getAttributionForEvent } from "@/utils/attribution";

const PHONE_QUERY = "(max-width: 767px)";

function hasCookie(name: string) {
  return document.cookie.split(";").some((cookie) => cookie.trim().startsWith(`${name}=`));
}

function setCookie(name: string, days: number) {
  document.cookie = `${name}=1;path=/;max-age=${days * 86400};SameSite=Lax${location.protocol === "https:" ? ";Secure" : ""}`;
}

export function useFreeBetsCapture(surface: string, onCaptured?: (email: string) => void) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<UsState | null>(null);
  const [stateSource, setStateSource] = useState<StateSource>("unknown");
  const [submitting, setSubmitting] = useState(false);
  const [known, setKnown] = useState(false);
  const [justCaptured, setJustCaptured] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const stateEditedRef = useRef(false);

  useEffect(() => {
    if (hasCookie(EMAIL_CAPTURED)) { setKnown(true); return; }
    if (typeof window.matchMedia !== "function" || !window.matchMedia(PHONE_QUERY).matches) return;
    const controller = new AbortController();
    fetch("/api/marketing/email-capture-context", { cache: "no-store", signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((context) => {
        if (!context) return;
        if (context.eligible !== true) { setKnown(true); return; }
        const detected = normalizeUsState(context.state);
        if (detected && !stateEditedRef.current) { setState(detected); setStateSource("detected"); }
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const onCapturedElsewhere = (event: Event) => {
      if ((event as CustomEvent).detail !== surface) setKnown(true);
    };
    window.addEventListener(EMAIL_CAPTURED_EVENT, onCapturedElsewhere);
    return () => window.removeEventListener(EMAIL_CAPTURED_EVENT, onCapturedElsewhere);
  }, [surface]);

  const changeEmail = (value: string) => {
    setEmail(value);
    setError(null);
  };

  const chooseState = (value: string) => {
    stateEditedRef.current = true;
    setState(normalizeUsState(value));
    setStateSource("selected");
  };

  const submit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    phCapture("email_capture_submitted", { surface });
    try {
      const saved = email.trim();
      const result = await captureCustomer(saved, {
        surface: EMAIL_CAPTURE_SURFACE, state, stateSource,
        ph_distinct_id: phGetDistinctId() ?? undefined, attribution: getAttributionForEvent(),
        event_source_url: window.location.href,
      });
      if (!result.success) {
        setError(result.outcome === "rate_limited"
          ? "Too many attempts. Please wait a few minutes and try again."
          : "We couldn't save your email. Please try again.");
        phCapture("email_capture_failed", { surface });
        return;
      }
      setCookie(EMAIL_CAPTURED, 365);
      setKnown(true);
      setJustCaptured(true);
      window.dispatchEvent(new CustomEvent(EMAIL_CAPTURED_EVENT, { detail: surface }));
      phCapture("email_capture_succeeded", { surface, outcome: result.outcome });
      onCaptured?.(saved);
    } catch {
      setError("We couldn't save your email. Please try again.");
      phCapture("email_capture_failed", { surface });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return { email, changeEmail, state, chooseState, submitting, known, justCaptured, error, submit };
}
